import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import FileUploadIcon from "@mui/icons-material/FileUploadOutlined";
import LanguageIcon from "@mui/icons-material/LanguageRounded";
import type { SelectChangeEvent } from "@mui/material";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers-pro";
import type { AxiosProgressEvent } from "axios";
import { useCallback, useRef, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import FilesDropArea from "@entrilia/shared/components/FilesDropArea";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import VideoFileIcon from "@entrilia/shared/icons/fileFormats/VideoFileIcon";
import { logError } from "@entrilia/shared/logging";
import { formatFileSize } from "@entrilia/shared/utilities/fileHelper";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../api/adminApi";
import type {
  VideoLibraryCategory,
  VideoLibraryItem,
  VideoLibraryUploadTarget,
} from "../../../api/types/videoLibraryTypes";
import BorderedRadioLabel from "../../common/BorderedRadioLabel";
import MultiselectAutocomplete from "../../common/MultiselectAutocomplete";
import type { VideoSourceType } from "./addVideoForm";
import {
  acceptedVideoFileExtensions,
  determineDurationFromFile,
  determineDurationFromUrl,
  getInitialAddVideoForm,
  mapFormToAddRequest,
  mapFormToFileUploadParams,
  maxVideoFileSize,
  validateForm,
} from "./addVideoForm";
import fileUploaderService from "./fileUploaderService";

interface Props {
  allCategories: VideoLibraryCategory[];
  existingVideoNames: string[];
  onClose: () => void;
  onAdded: (newItem: VideoLibraryItem) => void;
}

const addVideoToLibrary = withErrorHandling(adminApi.addVideoToLibrary);

const AddVideoDialog = ({ allCategories, existingVideoNames, onClose, onAdded }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const videoRef = useRef<HTMLVideoElement>(null);

  const [form, setForm] = useState(getInitialAddVideoForm);
  const [saving, setSaving] = useState(false);
  const [uploadProgressPercent, setUploadProgressPercent] = useState(0);

  const handleUploadProgress = useCallback((e: AxiosProgressEvent) => {
    setUploadProgressPercent((e.progress ?? 0) * 100);
  }, []);

  const addVideoFromLink = async () => {
    if (!videoRef?.current) {
      return;
    }

    const payload = mapFormToAddRequest(form);
    if (payload === undefined) {
      return;
    }

    setSaving(true);

    if (payload.target === "DirectLink") {
      const duration = await determineDurationFromUrl(form.videoLink, videoRef.current);
      videoRef.current.src = "";

      if (!duration) {
        setForm((prev) => ({ ...prev, invalidFileError: "Invalid video file URL" }));
        setSaving(false);
        return;
      }

      payload.duration = Math.round(duration);
    }

    const [newItem, error] = await addVideoToLibrary(payload);

    setSaving(false);

    if (error) {
      logError(error, "[AddVideoDialog] addVideoToLibrary");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Video added to library successfully");
    onAdded(newItem);
    onClose();
  };

  const addVideoFromFile = async () => {
    if (!videoRef?.current) {
      return;
    }

    const uploadParams = mapFormToFileUploadParams(form);
    if (uploadParams === undefined) {
      return;
    }

    setSaving(true);

    const duration = await determineDurationFromFile(uploadParams.file, videoRef.current);
    videoRef.current.src = "";
    if (!duration) {
      setForm((prev) => ({ ...prev, invalidFileError: "Invalid video file" }));
      setSaving(false);
      return;
    }

    uploadParams.duration = Math.round(duration);
    const [newItem, error] = await fileUploaderService.uploadVideoFileToLibrary(uploadParams, handleUploadProgress);

    setSaving(false);

    if (error) {
      logError(error, "[AddVideoDialog] uploadVideoFileToLibrary");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Video added to library successfully");
    onAdded(newItem);
    onClose();
  };

  const handleAddClick = async () => {
    if (form.sourceType === "link") {
      await addVideoFromLink();
    } else {
      await addVideoFromFile();
    }
  };

  const handleSourceTypeChange = (sourceType: VideoSourceType) => {
    setForm((prev) => ({
      ...prev,
      sourceType,
      videoLink: sourceType === "file" ? "" : prev.videoLink,
      file: sourceType === "link" ? undefined : prev.file,
      invalidFileError: "",
    }));
  };

  const handleTargetChange = (e: SelectChangeEvent<VideoLibraryUploadTarget | "">) => {
    setForm((prev) => ({
      ...prev,
      target: e.target.value,
      videoLink: "",
      invalidFileError: "",
    }));
  };

  const handleVideoLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({
      ...prev,
      videoLink: e.target.value,
      touchedFields: new Set([...prev.touchedFields, "videoLink"]),
    }));
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({
      ...prev,
      title: e.target.value,
      touchedFields: new Set([...prev.touchedFields, "title"]),
    }));
  };

  const handleCategoryChange = (selected: VideoLibraryCategory[]) => {
    setForm((prev) => ({
      ...prev,
      categories: selected,
      touchedFields: new Set([...prev.touchedFields, "categories"]),
    }));
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({
      ...prev,
      description: e.target.value,
    }));
  };

  const handleFilesAdd = (files: File[]) => {
    if (files.length === 0) {
      return;
    }

    const file = defined(files[0]);
    setForm((prev) => ({
      ...prev,
      file,
      invalidFileError: "",
      touchedFields: new Set([...prev.touchedFields, "file"]),
    }));
  };

  const handleRemoveFile = () => {
    setForm((prev) => ({ ...prev, file: undefined, duration: undefined, invalidFileError: "" }));
  };

  const { isFormValid, validationErrors } = validateForm(form, existingVideoNames);

  return (
    <Dialog open maxWidth="sm" fullWidth>
      <DialogTitle>Add Video</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="subtitle1">Source</Typography>

          <FormControl sx={{ mt: 0.5 }} fullWidth>
            <RadioGroup
              row
              defaultValue="link"
              value={form.sourceType}
              sx={{ columnGap: 1 }}
              onChange={(e) => handleSourceTypeChange(e.target.value as VideoSourceType)}
            >
              <BorderedRadioLabel<VideoSourceType>
                value="link"
                selectedValue={form.sourceType}
                label={
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <Avatar variant="rounded" sx={{ width: 32, height: 32 }}>
                      <LanguageIcon color="action" />
                    </Avatar>
                    <Typography>Provide a link</Typography>
                  </Stack>
                }
                sx={{ pl: 1, py: 1, flex: 1 }}
              />
              <BorderedRadioLabel<VideoSourceType>
                value="file"
                selectedValue={form.sourceType}
                label={
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <Avatar variant="rounded" sx={{ width: 32, height: 32 }}>
                      <FileUploadIcon color="action" />
                    </Avatar>
                    <Typography>Upload a file</Typography>
                  </Stack>
                }
                sx={{ pl: 1, py: 1, flex: 1 }}
              />
            </RadioGroup>
          </FormControl>

          {form.sourceType === "link" && (
            <FormControl fullWidth>
              <InputLabel id="add-video-target-select-label">Source</InputLabel>
              <Select
                labelId="add-video-target-select-label"
                label="Source"
                value={form.target}
                onChange={handleTargetChange}
              >
                <MenuItem value="SharePoint">SharePoint</MenuItem>
                <MenuItem value="DirectLink">Vimeo</MenuItem>
              </Select>
            </FormControl>
          )}

          {form.sourceType === "link" && (
            <TextField
              fullWidth
              label="URL"
              value={form.videoLink}
              onChange={handleVideoLinkChange}
              error={!!validationErrors.videoLink || !!form.invalidFileError}
              helperText={validationErrors.videoLink || form.invalidFileError}
            />
          )}

          {form.sourceType === "file" && form.file === undefined && (
            <FilesDropArea
              acceptedFileExtensions={acceptedVideoFileExtensions}
              subtitle={`Maximum file size: ${formatFileSize(
                maxVideoFileSize
              )}. Supported formats: mp4, mov, webm, ogv.`}
              onFilesAdd={handleFilesAdd}
            />
          )}

          {form.sourceType === "file" && form.file !== undefined && (
            <>
              <Paper variant="outlined">
                <Box sx={{ display: "flex", justifyContent: "space-between", p: 2 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center", pr: 1 }}>
                    <VideoFileIcon sx={{ width: 32, height: 32 }} />
                    <Typography noWrap>{form.file.name}</Typography>
                  </Stack>
                  <IconButton color="error" onClick={handleRemoveFile} disabled={saving}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Paper>

              {uploadProgressPercent > 0 && <LinearProgress variant="determinate" value={uploadProgressPercent} />}

              {(validationErrors.file || form.invalidFileError) && (
                <FormHelperText error>{validationErrors.file || form.invalidFileError}</FormHelperText>
              )}
            </>
          )}

          <Typography variant="subtitle1" sx={{ pt: 2 }}>
            Details
          </Typography>

          <TextField
            fullWidth
            label="Title"
            value={form.title}
            onChange={handleTitleChange}
            error={!!validationErrors.title}
            helperText={validationErrors.title}
          />

          <MultiselectAutocomplete
            options={allCategories}
            values={form.categories}
            onSelected={handleCategoryChange}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            placeholder="Select one or more categories"
            error={!!validationErrors.categories}
            helperText={validationErrors.categories}
          />

          <DateTimePicker
            label="Uploaded At"
            value={form.uploadedAt}
            onChange={(newValue) => {
              setForm((prev) => ({
                ...prev,
                uploadedAt: newValue,
              }));
            }}
            slotProps={{
              textField: {
                fullWidth: true,
              },
            }}
          />

          <TextField
            fullWidth
            multiline
            rows={5}
            label="Description (optional)"
            value={form.description}
            onChange={handleDescriptionChange}
          />
        </Stack>

        {/* Hidden element to determine video duration */}
        <video ref={videoRef} style={{ display: "none" }} preload="metadata" />
      </DialogContent>
      <DialogButtons onCancel={onClose} cancelDisabled={saving}>
        <Button variant="contained" loading={saving} onClick={handleAddClick} disabled={!isFormValid}>
          Add & Upload
        </Button>
      </DialogButtons>
    </Dialog>
  );
};

export default AddVideoDialog;
